import { useValuationNavigation } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationNavigation';
import { ValuationLocation } from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import { ValuationSummaryGroupByPersonRes } from '@dealstack/shared/lib/generated';
import { useCallback, useEffect, useMemo } from 'react';
import {
	buildPersonOptions,
	extractAvailableTags,
	findSelectedPerson,
	formatSelectedEntityName,
	getDefaultPersonSelection,
} from './personSelectionHelpers';

type PersonView = ValuationLocation.SOURCES_AND_USES | ValuationLocation.BREAKPOINT_SUMMARY;

interface UsePersonSelectionOptions {
	summaryByPersonData: ValuationSummaryGroupByPersonRes | undefined;
	view: PersonView;
	isSummaryFirstLoad: boolean;
}

export function usePersonSelection({
	summaryByPersonData,
	view,
	isSummaryFirstLoad,
}: UsePersonSelectionOptions) {
	const { dealId, pathValuationId, pathPersonId, pathMipId, navigateTo } =
		useValuationNavigation();

	const selectedPersonId = pathPersonId;
	const selectedMipId = pathMipId;

	useEffect(() => {
		if (pathPersonId) {
			return;
		}
		const defaultSelection = getDefaultPersonSelection(summaryByPersonData?.persons);
		if (!defaultSelection) {
			return;
		}
		navigateTo(view, {
			dealId: dealId ?? '',
			valuationId: pathValuationId ?? '',
			personId: defaultSelection.personId,
			mipId: defaultSelection.mipId,
		});
	}, [pathPersonId, summaryByPersonData, dealId, pathValuationId, navigateTo, view]);

	const selectedEntity = useMemo(
		() => findSelectedPerson(summaryByPersonData?.persons, selectedPersonId),
		[summaryByPersonData, selectedPersonId]
	);

	const personOptions = useMemo(
		() => buildPersonOptions(summaryByPersonData),
		[summaryByPersonData]
	);

	const availableTags = useMemo(
		() => extractAvailableTags(summaryByPersonData),
		[summaryByPersonData]
	);

	const loadPerson = useCallback(
		({ personId, mipId }: { personId: string; mipId?: string }) => {
			if (selectedPersonId === personId && selectedMipId === mipId) {
				return;
			}
			navigateTo(view, {
				dealId: dealId ?? '',
				valuationId: pathValuationId ?? '',
				personId,
				mipId,
			});
		},
		[selectedPersonId, selectedMipId, dealId, pathValuationId, navigateTo, view]
	);

	const selectedEntityName = formatSelectedEntityName(
		selectedEntity,
		selectedMipId,
		isSummaryFirstLoad
	);

	return {
		selectedPersonId,
		selectedMipId,
		selectedEntity,
		personOptions,
		availableTags,
		loadPerson,
		selectedEntityName,
		pathPersonId,
		pathMipId,
	};
}
